/* eslint-disable react/prop-types */
import { useTranslation } from 'i18n'
import React, { useEffect, useState } from 'react'

import { useSTheme } from 'styles/theme'

import ConditionalRender from 'utils/conditionalRender'

import { useFormContext } from 'react-hook-form'

import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'
import FImage from '../FImage'
import FLabel from '../FLabel'
import SFlex from 'components/opinionless/SFlex'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import { Input } from '@rebass/forms'
import { ga4_inputChanged } from 'utils/ga4'
import { LBButton } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { desktopThreshold } from '../constants'
import SColors from 'styles/colors'

type Props = {
    onUpload: (item: any) => void
    defaultDocument: any
    name: string
    id: string
    isUploading?: boolean
    alwaysUpload?: boolean
    multiple?: boolean
    canBeRemoved?: any
    onRemoveFile?: any
    reverseDocuments?: boolean
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB
const VALID_EXTENSIONS = ['.doc', '.docx', '.pdf', '.png', '.jpg', '.jpeg', '.heic', '.heif']

// eslint-disable-next-line react/display-name
const FFileUploadV2 = React.forwardRef((props: Props, ref) => {
    const { onUpload, defaultDocument } = props
    const { errors, setError, clearErrors } = useFormContext()
    const initialFiles = Array.isArray(defaultDocument) ? defaultDocument : []
    const [filesToBeUploaded, setFilesToBeUploaded] = useState(initialFiles)
    const [allowMultipleFiles, setAllowMultipleFiles] = useState(props.multiple)

    const [isMobile, setIsMobile] = useState(false)

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        setFilesToBeUploaded(defaultDocument || [])
        setAllowMultipleFiles(props.multiple)
    }, [defaultDocument, props.multiple])

    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()
    const fontFamily = theme.fontsV2[window.ORGANIZATION.name]?.body || theme.fontsV2.default

    const handleUpload = (e) => {
        if (e?.target?.files?.length > 0) {
            const newFiles = Array.from(e.target.files)
            const validFiles = newFiles.filter(isValidFile)

            if (validFiles.length > 0) {
                ga4_inputChanged(props.name, true)

                setFilesToBeUploaded((prevFiles) => {
                    if (!allowMultipleFiles) {
                        return validFiles.slice(-1)
                    } else {
                        return [...prevFiles, ...validFiles]
                    }
                })

                if (allowMultipleFiles) {
                    onUpload(validFiles)
                } else {
                    onUpload(validFiles[0])
                }
            }
        }
    }

    const isValidFile = (file) => {
        if (file) {
            if (file.size > MAX_FILE_SIZE) {
                alert('File size exceeds the maximum allowed limit (5MB). Please upload a smaller file.')
                return false
            }

            const re = /(?:\.([^.]+))?$/
            const extension = re.exec(file.name)[0]
            if (!VALID_EXTENSIONS.includes(extension)) {
                alert('Invalid file format. Allowed formats are: ' + VALID_EXTENSIONS.join(', '))
                return false
            }
        }

        return true
    }

    const handleButtonClick = () => {
        const fileInput = document.getElementById(props.id)
        if (fileInput) {
            fileInput.click()
        }
    }

    const handleRemoveFile = (fileIndex: number) => {
        const updatedFiles = [...filesToBeUploaded]
        updatedFiles.splice(fileIndex, 1)
        setFilesToBeUploaded(updatedFiles)
        const onlyFiles = updatedFiles.filter((item) => item instanceof File)
        props.onRemoveFile(onlyFiles, updatedFiles)
    }

    const renderFilesToBeUploaded = () => {
        return (
            <React.Fragment>
                {filesToBeUploaded.map((file, index) => (
                    <SFlex key={index} flexDirection={'row'} alignItems="center" style={{ position: 'relative' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                border: `1px solid ${theme.colors.gray400}`,
                                background: `${theme.colors.gray125}`,
                                padding: '12px',
                                width: '100%',
                                marginTop: props?.reverseDocuments ? 0 : '24px',
                                justifyContent: 'space-between',
                                marginBottom: props?.reverseDocuments ? '24px' : 0,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <FImage
                                    sx={{
                                        display: 'block',
                                        marginRight: '6px',
                                    }}
                                    width={'35px'}
                                    height={'auto'}
                                    imageUrl={'/lb_file_copy.svg'}
                                    alt={'some image'}
                                />
                                <FLabel
                                    for={`file-upload-${index}`}
                                    sx={{
                                        lineHeight: '24px',
                                        color: theme.colors.lbTitleColor,
                                        marginBottom: 0,
                                        fontWeight: '400',
                                        '&:hover': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                >
                                    {file?.documentOriginalName || file?.name}
                                </FLabel>
                            </div>
                            {props.canBeRemoved && (
                                <FImage
                                    width={'17px'}
                                    height={'25px'}
                                    imageUrl={'/lb_trash.svg'}
                                    alt={'some image'}
                                    sx={{
                                        '&:hover': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => handleRemoveFile(index)}
                                />
                            )}
                        </div>
                    </SFlex>
                ))}
            </React.Fragment>
        )
    }

    return (
        <div>
            <ConditionalRender if={!props.isUploading}>
                {filesToBeUploaded.length > 0 &&
                    !props.alwaysUpload &&
                    !props.reverseDocuments &&
                    renderFilesToBeUploaded()}
                {errors[props.name] && (
                    <SBox mt={'8px'} flex={1}>
                        <SInputError
                            sx={{
                                fontFamily: fontFamily,
                                color: theme.colors.redError,
                            }}
                            forElementId={props.id + '-container'}
                        >
                            {t(errors[props.name]?.message)}
                            {errors[props.name]?.message === 'format_file_upload_error'
                                ? VALID_EXTENSIONS.join(', ')
                                : ''}
                        </SInputError>
                    </SBox>
                )}
            </ConditionalRender>
            <LBButton
                onClick={() => handleButtonClick()}
                style={{ marginTop: '24px', border: '1px solid #2196F3' }}
                backgroundColor={SColors.Blue250}
                type="reset"
                loading={props.isUploading}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FImage
                        sx={{
                            display: 'block',
                            marginRight: '12px',
                        }}
                        width={'20px'}
                        height={'auto'}
                        imageUrl={'/lb_upload.svg'}
                        alt={'some image'}
                    />{' '}
                    {props.defaultDocument && !props.multiple ? t('replace_document') : t('upload_document')}
                    <Input
                        type="file"
                        name={props.name}
                        id={props.id}
                        accept={`${VALID_EXTENSIONS.join(', ')};capture=camera`}
                        onChange={handleUpload}
                        style={{
                            position: 'absolute',
                            opacity: 0,
                            height: '100%',
                            width: '100%',
                            maxWidth: '100%',
                            display: 'none',
                        }}
                        ref={ref}
                        multiple={props.multiple}
                    />
                </div>
            </LBButton>
            {filesToBeUploaded.length > 0 && !props.alwaysUpload && props.reverseDocuments && renderFilesToBeUploaded()}
        </div>
    )
})

FFileUploadV2.displayName = 'FFileUpload'

export default FFileUploadV2
